import React from 'react'
import PropTypes from 'prop-types'
import moment from 'moment'
import Table from '@material-ui/core/Table'
import TableBody from '@material-ui/core/TableBody'
import TableRow from '@material-ui/core/TableRow'
import InfoOutlinedIcon from '@material-ui/icons/InfoOutlined'
import Tooltip from '@material-ui/core/Tooltip'
import { fmt } from './formatters'
import { StyledTableHead } from '../ProjectsView.styles'
import {
  Section,
  SectionTitle,
  SectionSubtitle,
  OverviewTableContainer,
  HeaderCell,
  FirstHeaderCell,
  BodyCell,
  FirstBodyCell,
  RowLabel,
  Pill,
  CheckboxReadOnly,
  CheckboxCell,
} from './ProjectOverview.styles'

const DeltaCell = ({ value, currency }) => {
  if (value === 0) return <BodyCell>$0</BodyCell>
  return (
    <BodyCell>
      <Pill data-testid="pill" data-positive={value > 0} positive={value > 0}>
        {fmt(value, currency)}
      </Pill>
    </BodyCell>
  )
}

const InfoLabel = ({ label, tooltip }) => (
  <RowLabel>
    {label}
    {tooltip && (
      <Tooltip title={tooltip} placement="right">
        <InfoOutlinedIcon style={{ fontSize: 14, color: '#aaa' }} />
      </Tooltip>
    )}
  </RowLabel>
)

const TOOLTIPS = {
  Demand: 'Total revenue expected from the client for this project in this month.',
  'Capped Demand':
    'Whether the monthly demand is capped (fixed-budget month). Read-only from Salesforce.',
  Staffed: 'Total value of approved and submitted time entries for the month.',
  'Month to Date Submitted': 'Submitted time entries as of today in the current month.',
  Pacing: 'Projected total revenue based on current staffing trajectory through month end.',
  'Revenue Forecast': 'Projected revenue from Salesforce for this month.',
  'Over/Under-Staffed':
    'Staffed minus Demand. If > 0, request more staffing to capture demand. If < 0, request a budget increase from the client or work with Staffing to adjust team size.',
  'Over/Under-Pacing (against Demand)':
    'Pacing minus Demand. If > 0, request more staffing to capture demand. If < 0, request a budget increase from the client or work with Staffing to adjust team size.',
  'Over/Under-Pacing (against Staffed)':
    'Pacing minus Staffed. Positive = billing ahead of staffed baseline. Negative = billing behind.',
}

const MonthlyDemandTable = ({ months, projectEndDate, currency }) => {
  const formattedEndDate = projectEndDate ? moment(projectEndDate).format('MM/DD/YYYY') : '—'

  return (
    <Section>
      <SectionTitle>Monthly Demand</SectionTitle>
      <SectionSubtitle>Project End Date:&nbsp;&nbsp;{formattedEndDate}</SectionSubtitle>

      <OverviewTableContainer>
        <Table size="small">
          <StyledTableHead>
            <TableRow>
              <FirstHeaderCell />
              {months.map(m => (
                <HeaderCell key={m.month}>
                  {moment(m.month, 'YYYY-MM').format('MMM YYYY')}
                </HeaderCell>
              ))}
            </TableRow>
          </StyledTableHead>
          <TableBody>
            <TableRow>
              <FirstBodyCell>
                <InfoLabel label="Demand" tooltip={TOOLTIPS.Demand} />
              </FirstBodyCell>
              {months.map(m => (
                <BodyCell key={m.month}>{fmt(m.demand, currency)}</BodyCell>
              ))}
            </TableRow>
            <TableRow>
              <FirstBodyCell>
                <InfoLabel label="Capped Demand" tooltip={TOOLTIPS['Capped Demand']} />
              </FirstBodyCell>
              {months.map(m => (
                <BodyCell key={m.month}>
                  <CheckboxCell>
                    <CheckboxReadOnly checked={m.cappedDemand}>
                      {m.cappedDemand && '✓'}
                    </CheckboxReadOnly>
                  </CheckboxCell>
                </BodyCell>
              ))}
            </TableRow>
            <TableRow>
              <FirstBodyCell>
                <InfoLabel label="Staffed" tooltip={TOOLTIPS.Staffed} />
              </FirstBodyCell>
              {months.map(m => (
                <BodyCell key={m.month}>{fmt(m.staffed, currency)}</BodyCell>
              ))}
            </TableRow>
            <TableRow>
              <FirstBodyCell>
                <InfoLabel
                  label="Month to Date Submitted"
                  tooltip={TOOLTIPS['Month to Date Submitted']}
                />
              </FirstBodyCell>
              {months.map(m => (
                <BodyCell key={m.month}>{fmt(m.monthToDateSubmitted, currency)}</BodyCell>
              ))}
            </TableRow>
            <TableRow>
              <FirstBodyCell>
                <InfoLabel label="Pacing" tooltip={TOOLTIPS.Pacing} />
              </FirstBodyCell>
              {months.map(m => (
                <BodyCell key={m.month}>{fmt(m.pacing, currency)}</BodyCell>
              ))}
            </TableRow>
            <TableRow>
              <FirstBodyCell>
                <InfoLabel label="Revenue Forecast" tooltip={TOOLTIPS['Revenue Forecast']} />
              </FirstBodyCell>
              {months.map(m => (
                <BodyCell key={m.month}>{fmt(m.revenueForecast, currency)}</BodyCell>
              ))}
            </TableRow>
            <TableRow>
              <FirstBodyCell>
                <InfoLabel label="Over/Under-Staffed" tooltip={TOOLTIPS['Over/Under-Staffed']} />
              </FirstBodyCell>
              {months.map(m => (
                <DeltaCell key={m.month} value={m.staffed - m.demand} currency={currency} />
              ))}
            </TableRow>
            <TableRow>
              <FirstBodyCell>
                <InfoLabel
                  label="Over/Under-Pacing (against Demand)"
                  tooltip={TOOLTIPS['Over/Under-Pacing (against Demand)']}
                />
              </FirstBodyCell>
              {months.map(m => (
                <DeltaCell key={m.month} value={m.pacing - m.demand} currency={currency} />
              ))}
            </TableRow>
            <TableRow>
              <FirstBodyCell>
                <InfoLabel
                  label="Over/Under-Pacing (against Staffed)"
                  tooltip={TOOLTIPS['Over/Under-Pacing (against Staffed)']}
                />
              </FirstBodyCell>
              {months.map(m => (
                <DeltaCell key={m.month} value={m.pacing - m.staffed} currency={currency} />
              ))}
            </TableRow>
          </TableBody>
        </Table>
      </OverviewTableContainer>
    </Section>
  )
}

MonthlyDemandTable.propTypes = {
  months: PropTypes.arrayOf(
    PropTypes.shape({
      month: PropTypes.string.isRequired,
      demand: PropTypes.number.isRequired,
      cappedDemand: PropTypes.bool.isRequired,
      staffed: PropTypes.number.isRequired,
      monthToDateSubmitted: PropTypes.number.isRequired,
      pacing: PropTypes.number.isRequired,
      revenueForecast: PropTypes.number.isRequired,
    }),
  ).isRequired,
  projectEndDate: PropTypes.string,
  currency: PropTypes.string.isRequired,
}

export default MonthlyDemandTable
